import { useCallback, useEffect, useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { adminApi } from "../../lib/adminApi";

const Stat = ({ label, value, gold, testId }) => (
  <div className="rounded-3xl border border-cream/10 p-6" data-testid={testId}>
    <p className="text-xs font-bold uppercase tracking-[0.2em] text-cream/40">{label}</p>
    <p className={`mt-3 font-serif text-4xl ${gold ? "text-gold" : "text-cream"}`}>{value}</p>
  </div>
);

export const AnalyticsTab = () => {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const res = await adminApi.get("/admin/analytics");
    setData(res.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) return <p className="text-sm text-cream/50">Loading analytics…</p>;

  const chartData = data.signups_by_day.map((d) => ({
    ...d,
    label: new Date(d.date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }),
  }));
  const t = data.totals;
  const appMetrics = [
    ["Daily active users", "daily_active_users"],
    ["Matches made", "matches_made"],
    ["Voice notes sent", "voice_notes_sent"],
    ["Hearts+ active", "hearts_plus_active"],
  ];

  return (
    <div data-testid="analytics-tab">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Total members" value={t.members} gold testId="analytics-members" />
        <Stat label="Cities" value={t.cities} testId="analytics-cities" />
        <Stat label="Referrals made" value={t.total_referrals} testId="analytics-referrals" />
        <Stat label="Email opt-outs" value={t.opt_outs} testId="analytics-optouts" />
      </div>

      <div className="mt-8 rounded-3xl border border-cream/10 p-6" data-testid="analytics-chart">
        <p className="mb-5 text-xs font-bold uppercase tracking-[0.2em] text-cream/40">Signups — last 14 days</p>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C9A227" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#C9A227" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(247,244,237,0.06)" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "rgba(247,244,237,0.4)", fontSize: 11 }} axisLine={false} tickLine={false} interval={2} />
              <YAxis allowDecimals={false} tick={{ fill: "rgba(247,244,237,0.4)", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ backgroundColor: "#0A1526", border: "1px solid rgba(201,162,39,0.35)", borderRadius: "12px", fontSize: 13 }}
                labelStyle={{ color: "#F7F4ED" }}
                itemStyle={{ color: "#C9A227" }}
              />
              <Area type="monotone" dataKey="count" name="Signups" stroke="#C9A227" strokeWidth={2.5} fill="url(#goldFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <p className="mb-4 mt-10 text-xs font-bold uppercase tracking-[0.2em] text-cream/40">In-app metrics</p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {appMetrics.map(([label, key]) => (
          <div key={key} className="rounded-3xl border border-dashed border-cream/15 p-6" data-testid={`analytics-app-${key}`}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-cream/40">{label}</p>
            <p className="mt-3 font-serif text-2xl text-cream/35">—</p>
            <p className="mt-1 text-[11px] uppercase tracking-[0.15em] text-gold/50">Launches with the app</p>
          </div>
        ))}
      </div>
    </div>
  );
};
